import type { QueryClient, QueryKey } from '@tanstack/react-query'

import { queryClient } from '@/app/query-client'
import { marketApi } from '@/features/market/api/marketApi'
import { config } from '@/lib/config'

/**
 * A cache entry per RAW ENDPOINT, underneath the cache entries per SCREEN.
 *
 * ── The problem ─────────────────────────────────────────────────────────────
 *
 * Every service in features/<domain>/api/ composes a screen's payload by
 * fanning out to several raw endpoints inside one `queryFn`:
 *
 *   overviewService.overview()   → 10 endpoints, one of them /market/indices
 *   marketService.tickerTape()   →  2 endpoints, one of them /market/indices
 *   marketService.indexSummary() →  4 endpoints, one of them /market/indices
 *
 * React Query sees three query keys and three promises. It cannot see inside
 * them, so it has no idea those three fan-outs asked the same URL. There is no
 * shared cache entry and no in-flight dedup — `api/client.ts` coalesces
 * nothing either, deliberately, because it is an HTTP boundary and not a cache.
 *
 * On `/market` that cost 17 requests for 11 distinct URLs, every 20s tick
 * (`config.livePollMs`) and again on every window-focus refetch.
 *
 * ── The fix ─────────────────────────────────────────────────────────────────
 *
 * Give each raw endpoint call its own query key and let React Query fetch it.
 * `client.fetchQuery` is the non-hook door into the same cache the screens use,
 * so three fan-outs that reach for `/market/indices` now join ONE cache entry
 * and, while it is in flight, ONE promise.
 *
 * Nothing about the composition moves. A service still fans out in exactly the
 * same shape and still maps and validates its own payload; the only difference
 * is which door the bytes come through. No hook signature changes, no component
 * changes, no query key of any screen changes.
 *
 * ── Why this module lives in src/api/ ───────────────────────────────────────
 *
 * It is a property of the HTTP layer, not of any one feature: `/market/live` is
 * shared by floorsheet, depth, prices, company and watchlist, and none of them
 * is its owner. Putting the wrapper beside the client keeps that ownership
 * honest. It imports `marketApi` rather than re-declaring the calls so the
 * generated-schema types stay the single source of truth — a renamed wire field
 * must keep breaking the build in marketApi.ts and nowhere else.
 */

/**
 * Key root. Endpoint entries live in their own namespace so they can never
 * collide with a feature's keys, and so `marketKeys.all` (`['market']`) keeps
 * meaning exactly the set of screen-level entries it means today.
 *
 * That separation cuts both ways, and the cut is worth stating: a future
 * `invalidateQueries({ queryKey: marketKeys.all })` will mark the SCREEN
 * entries stale but not these, so the refetch it triggers could be served from
 * an endpoint entry up to ENDPOINT_STALE_MS old. Nothing in the app invalidates
 * market keys today. When something does, it must invalidate `[ENDPOINT_ROOT]`
 * alongside — that is what `invalidateEndpointCache` is for, and shipping this
 * module without wiring it is precisely how the manual-refresh affordances went
 * quiet; see `hooks/useManualRefresh.ts`.
 */
export const ENDPOINT_ROOT = 'endpoint'

/**
 * Half the live poll interval — 10s against `config.livePollMs` of 20s.
 *
 * The number has to sit in a window with a hard floor and a hard ceiling.
 *
 * CEILING — it must be strictly less than the poll interval, or the outer
 * query's 20s tick would be served from this cache and polling would quietly
 * stop meaning anything. At 10s every tick finds its endpoints stale and goes
 * to the network, which is the whole point of the poll. That is the constraint
 * that matters most, so the value is derived from `livePollMs` rather than
 * written as a literal: change the poll interval and this follows it.
 *
 * FLOOR — in-flight dedup alone (which a staleTime of 0 would still give us)
 * only collapses fan-outs that OVERLAP. Three queries polling on 20s timers
 * armed when each first settled drift apart by tens of milliseconds, and on a
 * fast connection a response can land before the next consumer asks. A window
 * measured in seconds covers that drift with room to spare.
 *
 * The freshness cost is nil in practice, and it is the same argument
 * `app/query-client.ts` already makes for its 30s default: mm-be refreshes live
 * prices on a 60s cron and depth on a 30s cron, so a re-request inside 10s
 * returns identical bytes. Data reaching a screen through this cache is at
 * worst 10s old — strictly fresher than the 30s the app already tolerates
 * everywhere for a mount-triggered refetch.
 */
const ENDPOINT_STALE_MS = config.livePollMs / 2

/**
 * Three poll intervals.
 *
 * `fetchQuery` creates no observer, so an endpoint entry is eligible for
 * collection the moment its fetch settles. It must outlive the gap between one
 * tick and the next, or the entry a consumer wants to share would already be
 * gone. Three intervals leaves margin for a slow response without pinning a
 * 200-row floorsheet payload in memory long after the member left the screen.
 */
const ENDPOINT_GC_MS = config.livePollMs * 3

/**
 * How often a DAY-LEVEL series is re-read while NEPSE trades.
 *
 * `/market/indices/{key}/history/` is one `stock_price` row per trading DATE.
 * Every point but today's is final, today's moves at most once a minute (the
 * backend's own cron), and none moves while NEPSE is shut. Under the live
 * window above it was re-read on every 20s tick all the same: the Overview's
 * index strip polls one history per index, so `/market` sent a `?range=1M`
 * request per index every tick — most of the page's traffic — for bytes that
 * could not have changed.
 *
 * Five minutes is the trade: today's point on a chart lags the live figure
 * printed above it by at most that, in exchange for fifteen times fewer
 * history requests. Exported for the hooks that poll ONLY a day-level series,
 * so they poll on this cadence rather than the live one.
 */
export const DAY_LEVEL_POLL_MS = 5 * 60_000

/**
 * Half the day-level poll, the same ratio ENDPOINT_STALE_MS keeps to
 * `livePollMs` and for the same reason: an entry must go stale before the
 * next tick of the hook polling it arrives, or that tick is answered out of
 * this cache and the poll silently halves. A live fan-out that reads a
 * day-level entry on its 20s tick therefore goes to the wire for it about
 * every two and a half minutes, and never while the market is shut, because
 * nothing polls then.
 */
const DAY_LEVEL_STALE_MS = DAY_LEVEL_POLL_MS / 2

/**
 * Past its freshness, plus the margin a live entry gets. `client.query` never
 * reschedules collection on a cache hit — only a fetch does — so an entry that
 * expired before it went stale would be dropped mid-window and refetched early.
 */
const DAY_LEVEL_GC_MS = DAY_LEVEL_STALE_MS + ENDPOINT_GC_MS

interface Freshness {
  staleTime: number
  gcTime: number
}

const LIVE: Freshness = { staleTime: ENDPOINT_STALE_MS, gcTime: ENDPOINT_GC_MS }
const DAY_LEVEL: Freshness = { staleTime: DAY_LEVEL_STALE_MS, gcTime: DAY_LEVEL_GC_MS }

/**
 * The argument list IS the cache key, with trailing `undefined` trimmed.
 *
 * The trim is not cosmetic. `depthService.sectors` asks for `heatmap()` while
 * `heatmapService` asks for `heatmap(undefined)` when no sector is selected.
 * Both produce the identical URL, but `['…','heatmap']` and
 * `['…','heatmap',undefined]` hash differently, so without this they would be
 * two entries and two requests — the exact bug this module exists to kill.
 *
 * React Query hashes object arguments with sorted keys, so `{type,limit}` and
 * `{limit,type}` are already one key.
 *
 * The limit of this scheme, stated so the next reader does not have to find it:
 * the key is the ARGUMENTS, not the resolved URL. Two call sites that reach the
 * same URL by different routes — one passing `brokers(50)`, one relying on
 * marketApi's `limit = 50` default — would not share. Every call site today is
 * consistent; a new one that is not should be normalised at the call site.
 */
const endpointKey = (name: string, args: readonly unknown[]): QueryKey => {
  let length = args.length
  while (length > 0 && args[length - 1] === undefined) length -= 1
  return [ENDPOINT_ROOT, 'market', name, ...args.slice(0, length)]
}

/**
 * Wraps one raw endpoint call in its own cache entry.
 *
 * `client.query` rather than `client.fetchQuery`: same function — build the
 * entry, return its data if fresh, otherwise fetch — but `fetchQuery` is
 * deprecated in query-core 5.102 and eslint fails the build on it. Both join
 * the in-flight promise of an entry that is already fetching, which is the
 * dedup this module is built on: `Query.fetch` returns the running retryer's
 * promise unless the caller asked to cancel, and neither of these does.
 *
 * A screen query composed entirely from fresh entries here still reaches its
 * component a frame late: the entries resolve at once, but React Query hands
 * the result to observers through a notification batched on `setTimeout(0)`,
 * so the first render sees `pending`. Nothing here can close that frame. What
 * keeps it from painting a skeleton is upstream: a route loader awaits its
 * page's main query before the page renders (`ensureRouteData`,
 * app/query-client.ts), and every card-level skeleton waits 80ms before it
 * is drawn (`useSkeletonTiming`).
 *
 * `retry: false` is load-bearing, not a default being restated. `query` only
 * defaults retry off when the client has no policy of its own, and ours has
 * one — two retries with exponential backoff. Today retry lives on the OUTER
 * query: a failed fan-out re-runs the whole `queryFn`. Inheriting the policy
 * here would nest one retry inside the other and turn a 5xx into up to nine
 * attempts on a single endpoint, with the backoff compounding. Retry stays
 * exactly where it already is.
 */
const endpoint =
  <TArgs extends readonly unknown[], TResult>(
    name: string,
    call: (...args: TArgs) => Promise<TResult>,
    freshness: Freshness = LIVE,
  ) =>
  (client: QueryClient) =>
  (...args: TArgs): Promise<TResult> =>
    client.query({
      queryKey: endpointKey(name, args),
      queryFn: () => call(...args),
      ...freshness,
      retry: false,
    })

const chartHistory = endpoint('chartHistory', marketApi.chartHistory)
const brokers = endpoint('brokers', marketApi.brokers)
const depth = endpoint('depth', marketApi.depth)
const events = endpoint('events', marketApi.events)
const fiftyTwoWeek = endpoint('fiftyTwoWeek', marketApi.fiftyTwoWeek)
const floorsheet = endpoint('floorsheet', marketApi.floorsheet)
const floorsheetSummary = endpoint('floorsheetSummary', marketApi.floorsheetSummary)
const heatmap = endpoint('heatmap', marketApi.heatmap)
const indices = endpoint('indices', marketApi.indices)
const indexHistory = endpoint('indexHistory', marketApi.indexHistory, DAY_LEVEL)
const live = endpoint('live', marketApi.live)
const movers = endpoint('movers', marketApi.movers)
const priceHistory = endpoint('priceHistory', marketApi.priceHistory)
const search = endpoint('search', marketApi.search)
const sector = endpoint('sector', marketApi.sector)
const sectors = endpoint('sectors', marketApi.sectors)
const sectorTurnover = endpoint('sectorTurnover', marketApi.sectorTurnover)
const status = endpoint('status', marketApi.status)
const summary = endpoint('summary', marketApi.summary)
const tradingAverage = endpoint('tradingAverage', marketApi.tradingAverage)
const updates = endpoint('updates', marketApi.updates)

/** Built once per client — see `marketEndpoints`. */
const byClient = new WeakMap<QueryClient, MarketEndpoints>()

export interface MarketEndpoints {
  chartHistory: ReturnType<typeof chartHistory>
  brokers: ReturnType<typeof brokers>
  depth: ReturnType<typeof depth>
  events: ReturnType<typeof events>
  fiftyTwoWeek: ReturnType<typeof fiftyTwoWeek>
  floorsheet: ReturnType<typeof floorsheet>
  floorsheetSummary: ReturnType<typeof floorsheetSummary>
  heatmap: ReturnType<typeof heatmap>
  indices: ReturnType<typeof indices>
  indexHistory: ReturnType<typeof indexHistory>
  live: ReturnType<typeof live>
  movers: ReturnType<typeof movers>
  priceHistory: ReturnType<typeof priceHistory>
  search: ReturnType<typeof search>
  sector: ReturnType<typeof sector>
  sectors: ReturnType<typeof sectors>
  sectorTurnover: ReturnType<typeof sectorTurnover>
  status: ReturnType<typeof status>
  summary: ReturnType<typeof summary>
  tradingAverage: ReturnType<typeof tradingAverage>
  updates: ReturnType<typeof updates>
}

/**
 * `marketApi`, with the same call signatures, reading through a cache.
 *
 * A service swaps `marketApi.indices()` for `api.indices()` and changes nothing
 * else — same arguments, same return type, same errors.
 *
 * ── The client argument, and why it is optional ─────────────────────────────
 *
 * The app mounts ONE QueryClient (`app/providers.tsx`), and a service called
 * from a `queryFn` has no way to reach it without either being handed it or
 * importing it. Defaulting to the module singleton is what lets this land
 * without touching a single `queries.ts` — the fan-out dedups today, with the
 * hooks exactly as they are.
 *
 * But a default is not a requirement, and tests must not be forced onto the
 * app's client. `test/query.ts` builds a fresh QueryClient per test on purpose,
 * so that cache state cannot leak from one test into the next; a service that
 * reached past it into the singleton would reintroduce precisely that leak, and
 * a 10s staleTime would hand test two the fixtures of test one. So every
 * service takes an optional `QueryClient` and passes it straight through here.
 * A test injects its own and stays isolated.
 *
 * The better long-term wiring is `queryFn: ({ client }) => svc.x(client)` —
 * React Query hands the queryFn the client that owns the query, which is the
 * right answer in a world with more than one. That is a change to every
 * `queries.ts`; the optional argument is the seam that makes it a one-line
 * change per hook whenever it happens.
 *
 * Memoised per client so a service call allocates no closures.
 */
export const marketEndpoints = (client: QueryClient = queryClient): MarketEndpoints => {
  const existing = byClient.get(client)
  if (existing !== undefined) return existing

  const built: MarketEndpoints = {
    chartHistory: chartHistory(client),
    brokers: brokers(client),
    depth: depth(client),
    events: events(client),
    fiftyTwoWeek: fiftyTwoWeek(client),
    floorsheet: floorsheet(client),
    floorsheetSummary: floorsheetSummary(client),
    heatmap: heatmap(client),
    indices: indices(client),
    indexHistory: indexHistory(client),
    live: live(client),
    movers: movers(client),
    priceHistory: priceHistory(client),
    search: search(client),
    sector: sector(client),
    sectors: sectors(client),
    sectorTurnover: sectorTurnover(client),
    status: status(client),
    summary: summary(client),
    tradingAverage: tradingAverage(client),
    updates: updates(client),
  }
  byClient.set(client, built)
  return built
}

/**
 * Marks every endpoint entry stale, so the next service call goes to the wire.
 *
 * The companion to a screen-level invalidation. `invalidateQueries` on a
 * feature's keys refetches the screen queries, but those refetches run the same
 * fan-out and would be served from here while the entries are still fresh.
 * Invalidating both is what makes "refresh this screen for real" true again.
 *
 * `hooks/useManualRefresh.ts` is its one caller, and it is the reason that hook
 * exists: an outer `query.refetch()` alone re-enters the fan-out, finds these
 * entries fresh, and settles without a single request — so for up to
 * ENDPOINT_STALE_MS after any poll tick, a member's Refresh click was a no-op
 * that still moved `dataUpdatedAt` and still printed "Updated just now".
 *
 * It invalidates the whole `[ENDPOINT_ROOT]` namespace rather than a subset
 * because the endpoints a screen depends on are only knowable inside its
 * service's fan-out; see that hook's own comment for the full argument, and for
 * why throwing this cache away on a deliberate click costs close to nothing.
 *
 * `refetchType` is left at its default of `'active'`, which matters and is easy
 * to misread as a gap: these entries are created by `client.query` and carry no
 * observer, so none of them is active and none is refetched HERE. They are only
 * marked stale — the refetch that follows is the screen's own, which is the one
 * that knows what to do with the bytes.
 */
export const invalidateEndpointCache = async (client: QueryClient = queryClient): Promise<void> => {
  await client.invalidateQueries({ queryKey: [ENDPOINT_ROOT] })
}
